// client/src/contexts/GameContext.jsx
import React, { createContext, useState, useEffect, useCallback } from 'react';
import gameService from '../api/gameService';
import socketService from '../api/socketService';
import { useAuth } from '../hooks/useAuth';

// Paramètres
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

// Créer le contexte
export const GameContext = createContext(null);

export const GameProvider = ({ children }) => {
  const [currentGame, setCurrentGame] = useState(null);
  
  // Persistance de la partie active (reprise apres refresh / F5)
  const ACTIVE_GAME_KEY = 'elementum_active_game';
  const persistActiveGame = (gameId) => {
    try { sessionStorage.setItem(ACTIVE_GAME_KEY, JSON.stringify({ gameId, at: Date.now() })); } catch (e) {}
  };
  const clearActiveGame = () => {
    try { sessionStorage.removeItem(ACTIVE_GAME_KEY); } catch (e) {}
  };
  const readActiveGame = () => {
    try {
      const raw = sessionStorage.getItem(ACTIVE_GAME_KEY);
      if (!raw) return null;
      const info = JSON.parse(raw);
      return info && info.gameId ? info : null;
    } catch (e) { return null; }
  };
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAIGame, setIsAIGame] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [connectionRetries, setConnectionRetries] = useState(0);
  // R34bb : récompenses de fin de partie (reçues du serveur via HTTP/socket)
  const [gameRewards, setGameRewards] = useState(null);
  
  const { currentUser } = useAuth();
  
  // Initialiser les sockets
  useEffect(() => {
    if (currentUser) {
      try {
        socketService.init();
        
        // S'abonner aux mises à jour de jeu
        const unsubscribe = socketService.onGameUpdate((data) => {
          // R34bb : ignorer les broadcasts hors partie active (après leaveGame)
          // sinon le dernier état d'une partie quittée ressuscite et coince
          // l'écran suivant (« En attente de l'état du jeu »).
          const active = readActiveGame();
          if (data && data.gameId && (!active || active.gameId !== data.gameId)) return;
          if (data && data.rewards) setGameRewards(data.rewards);
          if (data && data.gameState) {
            // Garde anti-retour-arrière : ne jamais régresser vers un état
            // plus ancien (broadcast intermédiaire arrivant après la réponse)
            setGameState((prev) => {
              if (!prev) return data.gameState;
              const prevSeq = prev.actionHistory?.length ?? 0;
              const newSeq = data.gameState.actionHistory?.length ?? 0;
              return newSeq >= prevSeq ? data.gameState : prev;
            });
          }
        });
        
        return () => {
          unsubscribe();
        };
      } catch (err) {
        console.warn("Erreur d'initialisation des sockets:", err);
        // Ne pas planter si les sockets échouent
      }
    }
  }, [currentUser]);
  
  // Créer une nouvelle partie
  const createNewGame = useCallback(async (deckId, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const { isAI = false, difficulty = 'medium' } = options;
      
      console.log("[GAME_CONTEXT] Création de partie:", { deckId, isAI, difficulty });
      
      // Appeler directement la fonction appropriée selon le type de partie
      let result;
      if (isAI) {
        // Ajout d'un log pour voir ce qui est envoyé
        console.log("[GAME_CONTEXT] Données envoyées pour partie IA:", {
          deckId,
          isAI: true,
          difficulty: difficulty || 'medium'
        });
        result = await gameService.createAIGame(deckId, {
          isAI: true,
          difficulty: difficulty || 'medium'
        });
      } else {
        result = await gameService.createGame(deckId);
      }
      
      if (result && result.gameId) {
        setGameRewards(null); // R34bb : nouvelle partie, ancien résultat oublié
        setCurrentGame(result.gameId);
        persistActiveGame(result.gameId);
        setIsAIGame(isAI || result.isAIGame);
        
        // Charger immédiatement l'état du jeu
        try {
          console.log(`[GAME_CONTEXT] Récupération immédiate de l'état pour ${result.gameId}`);
          const gameData = await gameService.getGameState(result.gameId);
          
          if (gameData && gameData.gameState) {
            console.log(`[GAME_CONTEXT] État initial récupéré avec succès pour ${result.gameId}`);
            setGameState(gameData.gameState);
            setIsOfflineMode(!!gameData.offline);
          } else {
            console.warn("[GAME_CONTEXT] État de jeu incomplet ou manquant après création");
            
            // Initialiser un état de jeu par défaut si nécessaire
            const offlineState = gameService.getGameEngine().initializeGame([], []);
            setGameState(offlineState);
            setIsOfflineMode(true);
          }
        } catch (stateError) {
          console.warn("[GAME_CONTEXT] Erreur lors de la récupération initiale de l'état:", stateError);
          
          // Initialiser un état de jeu par défaut en cas d'erreur
          const offlineState = gameService.getGameEngine().initializeGame([], []);
          setGameState(offlineState);
          setIsOfflineMode(true);
        }
        
        // Pour les parties IA, ne pas rediriger tout de suite pour donner le temps au serveur de traiter
        if (isAI) {
          console.log(`[GAME_CONTEXT] Délai court avant retour pour partie IA ${result.gameId}`);
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        return result;
      } else {
        throw new Error("La création de partie a échoué - Aucun ID de partie reçu");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la création de la partie.';
      console.error("[GAME_CONTEXT] Erreur de création:", errorMessage);
      setError(errorMessage);
      
      // Créer une partie hors ligne en cas d'erreur
      if (options.isAI) {
        console.warn("[GAME_CONTEXT] Création d'une partie IA hors ligne après erreur");
        const offlineGameId = `ai_offline_${Date.now()}`;
        const offlineState = gameService.getGameEngine().initializeGame([], []);
        
        setCurrentGame(offlineGameId);
        setGameState(offlineState);
        setIsAIGame(true);
        setIsOfflineMode(true);
        
        return {
          success: true,
          gameId: offlineGameId,
          isAIGame: true,
          offline: true
        };
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Rejoindre une partie existante
  const joinExistingGame = useCallback(async (gameId, deckId) => {
    try {
      // Vérifier que l'ID de partie est valide
      if (!gameId || gameId === 'undefined') {
        console.error('[GAME_CONTEXT] ID de partie invalide dans joinExistingGame:', gameId);
        setError('ID de partie invalide');
        return { success: false, message: 'ID de partie invalide' };
      }
      
      // Détecter si c'est une partie IA à partir de l'ID (préfixe serveur: ai_<uuid>)
      const isAIGameId = gameId.startsWith('ai_');
      
      setLoading(true);
      setError(null);
      setConnectionRetries(0);
      
      console.log(`[GAME_CONTEXT] Tentative de rejoindre la partie: ${gameId} (IA: ${isAIGameId})`);
      
      // IMPORTANT: Définir currentGame immédiatement
      setCurrentGame(gameId);
      persistActiveGame(gameId);
      
      // Pour les parties IA, pas besoin d'appeler joinGame (déjà créateur)
      // mais il FAUT rejoindre la salle socket pour recevoir les mises à jour
      let result;
      if (isAIGameId) {
        console.log(`[GAME_CONTEXT] Partie IA détectée, pas besoin de rejoindre (HTTP)`);
        try { socketService.joinGame(gameId); } catch (e) { /* non bloquant */ }
        result = { success: true, gameId, isAIGame: true };
      } else {
        result = await gameService.joinGame(gameId, deckId);
      }
      
      // Vérifier si la requête a réussi
      if (!result || !result.success) {
        const errorMsg = result?.message || 'Erreur de connexion à la partie';
        setError(errorMsg);
        throw new Error(errorMsg);
      }
      
      setIsAIGame(isAIGameId || result.isAIGame);
      
      // Utiliser le mode fallback si la récupération d'état échoue
      try {
        console.log(`[GAME_CONTEXT] Récupération de l'état de la partie: ${gameId}`);
        const gameData = await gameService.getGameState(gameId);
        
        if (gameData && gameData.gameState) {
          console.log(`[GAME_CONTEXT] État récupéré avec succès pour ${gameId}`);
          setGameState(gameData.gameState);
          setIsAIGame(gameData.isAIGame || isAIGameId);
          return result; // Retourner le résultat ici si tout est OK
        } else {
          console.warn(`[GAME_CONTEXT] État de jeu incomplet ou manquant pour ${gameId}, tentative de mode hors ligne`);
          
          // Initialiser un état de base pour le mode hors ligne
          const offlineState = gameService.getGameEngine().initializeGame([], []);
          setGameState(offlineState);
          setIsOfflineMode(true);
          
          // Important: retourner un résultat même en mode hors ligne
          return {
            ...result,
            offline: true,
            gameState: offlineState
          };
        }
      } catch (stateError) {
        console.error(`[GAME_CONTEXT] Erreur lors de la récupération de l'état du jeu ${gameId}:`, stateError);
        
        // Activer le mode hors ligne en cas d'erreur
        console.log(`[GAME_CONTEXT] Activation du mode hors ligne suite à une erreur pour ${gameId}`);
        const offlineState = gameService.getGameEngine().initializeGame([], []);
        setGameState(offlineState);
        setIsOfflineMode(true);
        
        // Important: retourner un résultat même après une erreur
        return {
          ...result,
          offline: true, 
          gameState: offlineState
        };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la connexion à la partie.';
      console.error(`[GAME_CONTEXT] Erreur de connexion à ${gameId}:`, errorMessage);
      
      // Basculer en mode hors ligne en cas d'erreur pour éviter de bloquer l'utilisateur
      const offlineState = gameService.getGameEngine().initializeGame([], []);
      setGameState(offlineState);
      setIsOfflineMode(true);
      setCurrentGame(gameId);
      
      return {
        success: false,
        message: errorMessage,
        offline: true,
        gameState: offlineState
      };
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Reprendre la partie persistée après un refresh (F5).
  // Retourne true si une partie a été restaurée, false sinon.
  const resumeActiveGame = useCallback(async () => {
    const info = readActiveGame();
    if (!info) return false;
    const { gameId } = info;
    console.log(`[GAME_CONTEXT] Reprise de la partie persistée: ${gameId}`);
    try {
      const gameData = await gameService.getGameState(gameId);
      if (gameData && gameData.gameState && !gameData.gameState.isGameOver) {
        setCurrentGame(gameId);
        setGameState(gameData.gameState);
        setIsAIGame(gameData.isAIGame || gameId.startsWith('ai_'));
        if (gameData.offline !== undefined) setIsOfflineMode(gameData.offline);
        try { socketService.joinGame(gameId); } catch (e) { /* non bloquant */ }
        return true;
      }
      // Partie terminée ou introuvable côté état -> nettoyer
      clearActiveGame();
      return false;
    } catch (err) {
      console.warn(`[GAME_CONTEXT] Reprise impossible pour ${gameId}:`, err.message);
      clearActiveGame();
      return false;
    }
  }, []);
  
  // Y a-t-il une partie persistée (refresh/F5) en attente de reprise ?
  const hasActiveGame = useCallback(() => !!readActiveGame(), []);

  // Quitter la partie actuelle
  const leaveGame = useCallback(() => {
    if (currentGame) {
      console.log(`[GAME_CONTEXT] Quittant la partie ${currentGame}, isOfflineMode=${isOfflineMode}`);
      try {
        // N'essayer d'émettre l'événement socket que si nous ne sommes pas en mode hors ligne
        if (!isOfflineMode) {
          // Passer skipSurrender=true car nous gérons l'abandon explicitement ici
          socketService.leaveGame(currentGame, { skipSurrender: true });
          
          // Si c'est une partie en ligne, informer le serveur explicitement
          if (!currentGame.includes('offline')) {
            console.log(`[GAME_CONTEXT] Abandon explicite de la partie ${currentGame}`);
            gameService.surrenderGame(currentGame).catch(err => {
              console.warn(`[GAME_CONTEXT] Erreur lors de l'abandon de la partie ${currentGame}:`, err);
            });
          }
        }
      } catch (err) {
        console.warn("[GAME_CONTEXT] Erreur lors de la déconnexion:", err);
      }
    }
    
    setCurrentGame(null);
    setGameState(null);
    clearActiveGame();
    setIsAIGame(false);
    setIsOfflineMode(false);
    setConnectionRetries(0);
    setError(null);
    setGameRewards(null); // R34bb
  }, [currentGame, isOfflineMode]);
  
  // Rafraîchir l'état de la partie
  const refreshGameState = useCallback(async () => {
    if (!currentGame) {
      console.warn("Tentative de rafraîchissement sans partie active");
      return false;
    }
    
    try {
      console.log(`[GAME_CONTEXT] Rafraîchissement de l'état pour: ${currentGame}`);
      const gameData = await gameService.getGameState(currentGame);
      
      if (gameData && gameData.gameState) {
        console.log(`[GAME_CONTEXT] État rafraîchi avec succès pour ${currentGame}`);
        
        // Mise à jour de l'état
        setGameState(gameData.gameState);
        
        // Mise à jour du mode hors ligne
        if (gameData.offline !== undefined) {
          setIsOfflineMode(gameData.offline);
        }
        
        // Mise à jour du type de partie
        if (gameData.isAIGame !== undefined) {
          setIsAIGame(gameData.isAIGame);
        }
        
        setConnectionRetries(0); // Réinitialiser les compteurs de tentatives
        return true;
      } else {
        console.warn(`[GAME_CONTEXT] État incomplet ou manquant pour ${currentGame}`);
        
        // Si la partie a été recréée, mais que nous n'avons pas l'état, essayer de nouveau
        if (gameData && gameData.recreated) {
          console.log(`[GAME_CONTEXT] La partie ${currentGame} a été recréée, nouvelle tentative...`);
          // Attendre un peu plus longtemps pour que le serveur ait le temps de finaliser la recréation
          await new Promise(resolve => setTimeout(resolve, 1000));
          return refreshGameState();
        }
        
        // Activer le mode hors ligne si aucun état valide n'est trouvé
        if (!isOfflineMode) {
          console.log(`[GAME_CONTEXT] Activation du mode hors ligne suite à un état manquant pour ${currentGame}`);
          const offlineState = gameService.getGameEngine().initializeGame([], []);
          setGameState(offlineState);
          setIsOfflineMode(true);
        }
        
        return false;
      }
    } catch (err) {
      console.error(`[GAME_CONTEXT] Erreur lors du rafraîchissement de ${currentGame}:`, err);
      
      // Gestion des nouvelles tentatives
      if (connectionRetries < MAX_RETRIES) {
        setConnectionRetries(prev => prev + 1);
        console.log(`[GAME_CONTEXT] Tentative ${connectionRetries + 1}/${MAX_RETRIES} dans ${RETRY_DELAY}ms...`);
        
        // Programmer une nouvelle tentative
        setTimeout(() => {
          refreshGameState().catch(e => {
            console.warn(`[GAME_CONTEXT] Échec de la tentative de rafraîchissement automatique pour ${currentGame}:`, e);
          });
        }, RETRY_DELAY);
      } else {
        setError("Impossible de se connecter au serveur après plusieurs tentatives.");
        
        // Activer le mode hors ligne après épuisement des tentatives
        if (!isOfflineMode) {
          console.log(`[GAME_CONTEXT] Activation du mode hors ligne après échec des tentatives pour ${currentGame}`);
          const offlineState = gameService.getGameEngine().initializeGame([], []);
          setGameState(offlineState);
          setIsOfflineMode(true);
        }
      }
      
      return false;
    }
  }, [currentGame, connectionRetries, isOfflineMode]);

  // R34mm2 : le socket s'est reconnecté et a re-joint la room — l'état
  // local est potentiellement périmé (on a raté des game-update pendant
  // la coupure). On refetch l'état serveur immédiatement.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onRejoined = () => {
      if (currentGame) refreshGameState().catch(() => {});
    };
    window.addEventListener('elementum:socket-rejoined', onRejoined);
    return () => window.removeEventListener('elementum:socket-rejoined', onRejoined);
  }, [currentGame, refreshGameState]);
  
  // Effectuer une action dans la partie
  const performGameAction = useCallback(async (action, data = {}) => {
    if (!currentGame) {
      console.error("[GAME_CONTEXT] Tentative d'action sans partie active");
      return;
    }
    
    try {
      setLoading(true);
      console.log(`[GAME_CONTEXT] Exécution de l'action ${action} avec les données:`, data, 
                  `Partie: ${currentGame}, IA: ${isAIGame}, Phase: ${gameState?.phase}`);
      
      // Ajouter l'état actuel pour les fallbacks côté client
      const result = await gameService.performAction(currentGame, action, {
        ...data,
        currentState: gameState
      });
      
      if (result && result.gameState) {
        if (result.rewards) setGameRewards(result.rewards); // R34bb
        console.log(`[GAME_CONTEXT] Action ${action} réussie, mise à jour de l'état`);
        
        // Important: vérifier si c'est une partie IA et si c'est au tour de l'IA
        let finalState = result.gameState;
        
        // Filet de sécurité: le serveur joue normalement le tour de l'IA dans
        // sa réponse. S'il ne l'a pas fait (conditions identiques à celles du
        // serveur), le déclencher — SANS jamais fabriquer d'état côté client.
        if (
          isAIGame &&
          finalState.activePlayer === 'opponent' &&
          finalState.phase === 'main' &&
          !finalState.pendingAction &&
          !finalState.isGameOver
        ) {
          console.log("[GAME_CONTEXT] Tour de l'IA non joué par le serveur, envoi AI_TURN");
          try {
            await new Promise(resolve => setTimeout(resolve, 600));
            const aiResult = await gameService.performAction(currentGame, 'AI_TURN', {
              currentState: finalState
            });
            if (aiResult && aiResult.gameState) {
              finalState = aiResult.gameState;
            }
          } catch (aiError) {
            // On garde l'état serveur tel quel — aucune fabrication locale
            console.warn("[GAME_CONTEXT] Erreur lors du tour de l'IA:", aiError.message);
          }
        }        
        
        setGameState(finalState);
        
        // Mise à jour du mode hors ligne
        if (result.offline !== undefined) {
          setIsOfflineMode(result.offline);
        }
        
        return { ...result, gameState: finalState };
      } else {
        console.error(`[GAME_CONTEXT] Action ${action} n'a pas retourné d'état de jeu valide`);
        throw new Error("État de jeu invalide après action");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de l\'exécution de l\'action.';
      console.error("[GAME_CONTEXT] Erreur d'action:", errorMessage);

      // R34aa : partie perdue côté serveur (redémarrage/déploiement) —
      // retour propre à l'accueil au lieu d'un crash "filter of undefined".
      const status = err.response?.status;
      if (status === 404 || String(errorMessage).includes('Partie introuvable')) {
        setError('Partie introuvable côté serveur (redémarrage ou expiration).');
        clearActiveGame();
        setCurrentGame(null);
        setGameState(null);
        try {
          window.dispatchEvent(new CustomEvent('elementum:game-lost', { detail: { reason: 'missing' } }));
        } catch (e) { /* noop */ }
        return { success: false, fatal: true };
      }
      
      // Fallback mock UNIQUEMENT pour les parties locales (id contenant 'offline').
      // Pour une partie serveur (ai_/pvp_), une erreur doit être affichée — jamais
      // remplacée par un état fabriqué qui désynchroniserait tout.
      const isLocalOfflineGame = currentGame && String(currentGame).includes('offline');
      if (isLocalOfflineGame && 
         (err.message && err.message.includes('Network Error'))) {
        
        console.warn("[GAME_CONTEXT] Traitement de l'action en mode fallback");
        
        try {
          // Utiliser le moteur de jeu mock pour traiter l'action
          const mockEngine = gameService.getGameEngine();
          let updatedState = { ...gameState };
          
          switch (action) {
            case 'END_PHASE':
            case 'NEXT_PHASE':
              updatedState = mockEngine.nextPhase(updatedState);
              break;
            case 'SUMMON':
              if (data.cardId && data.position !== undefined) {
                updatedState = mockEngine.summonCharacter(
                  updatedState,
                  'player',
                  data.cardId,
                  data.position
                );
              }
              break;
            // Autres actions...
            default:
              console.warn(`[GAME_CONTEXT] Action non gérée en mode fallback: ${action}`);
              // Aucune modification à l'état
              break;
          }
          
          // Si c'est une partie IA et que c'est au tour de l'IA
          if (isAIGame && updatedState.activePlayer === 'opponent') {
            const mockAI = gameService.getAIPlayer();
            updatedState = mockAI.playAITurn(updatedState);
          }
          
          setGameState(updatedState);
          setIsOfflineMode(true);
          
          return {
            success: true,
            gameState: updatedState,
            offline: true
          };
        } catch (fallbackError) {
          console.error("[GAME_CONTEXT] Échec du fallback d'action:", fallbackError);
        }
      }
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentGame, gameState, isAIGame, isOfflineMode]);
  
  // Abandonner la partie
  const surrenderGame = useCallback(async () => {
    if (!currentGame) return;
    
    try {
      setLoading(true);
      
      const result = await gameService.surrenderGame(currentGame);
      
      leaveGame();
      
      return result;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de l\'abandon de la partie.';
      console.error("[GAME_CONTEXT] Erreur d'abandon:", errorMessage);
      
      // Si l'erreur est liée au réseau, quitter quand même la partie
      leaveGame();
      
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentGame, leaveGame]);
  
  // Vérifier si c'est le tour du joueur
  const isPlayerTurn = gameState?.activePlayer === 'player';
  
  // Partie terminée -> ne plus proposer de reprise au prochain refresh
  useEffect(() => {
    if (gameState && gameState.isGameOver) {
      clearActiveGame();
    }
  }, [gameState]);
  
  // Valeur du contexte
  const value = {
    currentGame,
    gameState,
    loading,
    error,
    isAIGame,
    isPlayerTurn,
    isOfflineMode,
    createNewGame,
    joinExistingGame,
    resumeActiveGame,
    hasActiveGame,
    leaveGame,
    refreshGameState,
    performGameAction,
    surrenderGame,
    gameRewards
  };
  
  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};